<template>
  <v-container class="my-5">
    <!-- Diálogo para Agregar / Editar Área Común -->
    <v-dialog v-model="dialog" persistent max-width="650px">
      <v-card class="elevation-5">
        <v-toolbar dark color="primary">
          <v-toolbar-title>
            <v-icon left dark>{{ isEditing ? 'mdi-pencil' : 'mdi-plus' }}</v-icon>
            {{ isEditing ? $t('edit_common_area') : $t('add_common_area') }}
          </v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon dark @click="closeDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-form ref="formRef" v-model="valid" @submit.prevent="saveArea">
          <v-card-text class="pt-4">
            <v-row dense>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="form.name"
                  :label="'* ' + $t('name_common_area')"
                  placeholder="Ej. Alberca, Gimnasio, Terraza"
                  outlined
                  dense
                  :rules="[v => !!v || 'El nombre es obligatorio']"
                  prepend-inner-icon="mdi-format-title"
                ></v-text-field>
              </v-col>

              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="form.location"
                  :label="$t('location_common_area')"
                  placeholder="Ej. Zona Norte, Edificio B"
                  outlined
                  dense
                  prepend-inner-icon="mdi-map-marker"
                ></v-text-field>
              </v-col>

              <!-- ====================================================== -->
              <!-- SECCIÓN MAPA GOOGLE MAPS                               -->
              <!-- ====================================================== -->
              <v-col cols="12">
                <v-card outlined class="rounded-lg map-section-card">
                  <v-card-title class="subtitle-1 font-weight-bold pb-0">
                    <v-icon left color="primary">mdi-map-marker-radius</v-icon>
                    Ubicación en Mapa
                    <v-spacer></v-spacer>
                    <v-btn
                      small
                      outlined
                      color="primary"
                      :loading="geoLocating"
                      @click="useMyLocation"
                    >
                      <v-icon left small>mdi-crosshairs-gps</v-icon>
                      Mi ubicación
                    </v-btn>
                  </v-card-title>

                  <v-card-text class="pb-2">
                    <div
                      ref="mapCanvas"
                      class="map-canvas"
                      :class="{ 'map-canvas--loading': mapLoading }"
                    >
                      <div v-if="mapLoading" class="map-placeholder">
                        <v-progress-circular indeterminate color="primary" size="40"></v-progress-circular>
                        <span class="caption ml-3 grey--text">Cargando mapa...</span>
                      </div>
                    </div>

                    <p class="caption grey--text mt-2 mb-0">
                      <v-icon x-small color="grey">mdi-information-outline</v-icon>
                      Haz clic en el mapa para colocar el marcador. Los campos se rellenarán automáticamente.
                    </p>
                  </v-card-text>

                  <v-card-text class="pt-0">
                    <v-row dense>
                      <v-col cols="12" sm="5">
                        <v-text-field
                          v-model.number="form.latitude"
                          label="Latitud"
                          type="number"
                          step="any"
                          outlined
                          dense
                          prepend-inner-icon="mdi-latitude"
                          placeholder="Ej. 19.4326"
                          @input="onLatLngInput"
                        ></v-text-field>
                      </v-col>

                      <v-col cols="12" sm="5">
                        <v-text-field
                          v-model.number="form.longitude"
                          label="Longitud"
                          type="number"
                          step="any"
                          outlined
                          dense
                          prepend-inner-icon="mdi-longitude"
                          placeholder="Ej. -99.1332"
                          @input="onLatLngInput"
                        ></v-text-field>
                      </v-col>

                      <v-col cols="12" sm="2" class="d-flex align-center">
                        <v-tooltip bottom>
                          <template v-slot:activator="{ on, attrs }">
                            <v-btn
                              icon
                              color="error"
                              v-bind="attrs"
                              v-on="on"
                              :disabled="!form.latitude && !form.longitude"
                              @click="clearMapLocation"
                            >
                              <v-icon>mdi-map-marker-off</v-icon>
                            </v-btn>
                          </template>
                          <span>Limpiar ubicación</span>
                        </v-tooltip>
                      </v-col>

                      <v-col cols="12" v-if="form.location">
                        <v-text-field
                          :value="form.location"
                          label="Link de Google Maps (guardado)"
                          outlined
                          dense
                          readonly
                          prepend-inner-icon="mdi-google-maps"
                          append-icon="mdi-open-in-new"
                          @click:append="openMapLink"
                        ></v-text-field>
                      </v-col>
                    </v-row>
                  </v-card-text>
                </v-card>
              </v-col>

              <v-col cols="12">
                <v-textarea
                  v-model="form.description"
                  :label="$t('description_common_area')"
                  placeholder="Descripción breve de la área común..."
                  outlined
                  dense
                  rows="2"
                  prepend-inner-icon="mdi-card-text-outline"
                ></v-textarea>
              </v-col>

              <v-col cols="12" sm="4">
                <v-text-field
                  v-model.number="form.capacity"
                  :label="$t('capacity_common_area')"
                  type="number"
                  min="0"
                  outlined
                  dense
                  prepend-inner-icon="mdi-account-group"
                ></v-text-field>
              </v-col>

              <v-col cols="12" sm="4">
                <v-text-field
                  v-model.number="form.maxHoursPerReservation"
                  :label="$t('max_hours_per_reservation')"
                  type="number"
                  min="1"
                  outlined
                  dense
                  prepend-inner-icon="mdi-clock-outline"
                ></v-text-field>
              </v-col>

              <v-col cols="12" sm="4">
                <v-text-field
                  v-model.number="form.reservationIntervalMinutes"
                  :label="$t('reservation_interval')"
                  type="number"
                  min="15"
                  step="15"
                  outlined
                  dense
                  prepend-inner-icon="mdi-timer-sand"
                ></v-text-field>
              </v-col>

              <v-col cols="12" sm="6">
                <v-menu
                  v-model="colorMenu"
                  :close-on-content-click="false"
                  nudge-right="40"
                  transition="scale-transition"
                  offset-y
                  min-width="290px"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="form.colorCalendar"
                      :label="$t('color_calendar')"
                      readonly
                      outlined
                      dense
                      v-bind="attrs"
                      v-on="on"
                      prepend-inner-icon="mdi-palette"
                    >
                      <template v-slot:append>
                        <v-avatar size="24" :style="{ backgroundColor: form.colorCalendar || '#3498DB' }"></v-avatar>
                      </template>
                    </v-text-field>
                  </template>
                  <v-color-picker
                    v-model="form.colorCalendar"
                    mode="hex"
                    hide-mode-switch
                  ></v-color-picker>
                </v-menu>
              </v-col>

              <v-col cols="12" sm="6">
                <v-file-input
                  v-model="imageFile"
                  label="Imagen del Área Común"
                  accept="image/*"
                  outlined
                  dense
                  prepend-icon=""
                  prepend-inner-icon="mdi-camera"
                  placeholder="Selecciona una imagen"
                  @change="onFileChange"
                ></v-file-input>
              </v-col>

              <v-col cols="12" v-if="imagePreview">
                <div class="subtitle-2 mb-1">Vista Previa de Imagen:</div>
                <v-img
                  :src="imagePreview"
                  max-height="160"
                  contain
                  class="grey lighten-3 rounded"
                ></v-img>
              </v-col>

              <v-col cols="12" sm="4">
                <v-switch
                  v-model="form.hasPrice"
                  :label="$t('has_price')"
                  color="primary"
                  dense
                  hide-details
                ></v-switch>
              </v-col>

              <v-col cols="12" sm="8" v-if="form.hasPrice">
                <v-text-field
                  v-model.number="form.price"
                  :label="$t('price')"
                  type="number"
                  min="0"
                  step="0.5"
                  outlined
                  dense
                  prepend-inner-icon="mdi-currency-usd"
                ></v-text-field>
              </v-col>

              <v-col cols="12" sm="6">
                <v-switch
                  v-model="form.requiresAuthorization"
                  :label="$t('requires_authorization')"
                  color="primary"
                  dense
                  hide-details
                ></v-switch>
              </v-col>

              <v-col cols="12" sm="6">
                <v-switch
                  v-model="form.active"
                  :label="$t('active')"
                  color="success"
                  dense
                  hide-details
                ></v-switch>
              </v-col>

              <!-- Horario de Apertura -->
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="form.openingTime"
                  label="Horario de Apertura (HH:MM)"
                  placeholder="08:00"
                  outlined
                  dense
                  prepend-inner-icon="mdi-clock-start"
                  :rules="[v => !v || /^([01]\d|2[0-3]):[0-5]\d$/.test(v) || 'Formato HH:MM requerido']"
                  hint="Ej. 08:00"
                  persistent-hint
                ></v-text-field>
              </v-col>

              <!-- Horario de Cierre -->
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="form.closingTime"
                  label="Horario de Cierre (HH:MM)"
                  placeholder="22:00"
                  outlined
                  dense
                  prepend-inner-icon="mdi-clock-end"
                  :rules="[v => !v || /^([01]\d|2[0-3]):[0-5]\d$/.test(v) || 'Formato HH:MM requerido']"
                  hint="Ej. 22:00"
                  persistent-hint
                ></v-text-field>
              </v-col>

              <!-- Política de Invitados -->
              <v-col cols="12">
                <v-select
                  v-model="form.guestPolicy"
                  :items="guestPolicyOptions"
                  label="Política de Invitados"
                  outlined
                  dense
                  prepend-inner-icon="mdi-account-multiple"
                ></v-select>
              </v-col>

            </v-row>
          </v-card-text>

          <v-divider></v-divider>

          <v-card-actions class="pa-4">
            <v-btn color="grey lighten-1" text @click="closeDialog">
              Cancelar
            </v-btn>
            <v-spacer></v-spacer>
            <v-btn
              color="primary"
              type="submit"
              :loading="submitting"
              :disabled="!valid"
            >
              {{ isEditing ? $t('save_changes') : $t('create_common_area') }}
            </v-btn>
          </v-card-actions>
        </v-form>
      </v-card>
    </v-dialog>

    <!-- Diálogo de Confirmación de Eliminación -->
    <v-dialog v-model="deleteDialog" max-width="420px" persistent>
      <v-card>
        <v-card-title class="headline error--text">
          <v-icon left color="error">mdi-alert-circle</v-icon>
          Confirmar Eliminación
        </v-card-title>
        <v-card-text class="pt-2">
          ¿Está seguro de que desea eliminar el área común
          <strong>"{{ itemToDelete ? itemToDelete.name : '' }}"</strong>?
          <br />
          <span class="caption red--text">Esta acción no se puede deshacer.</span>
        </v-card-text>
        <v-card-actions class="pa-4">
          <v-btn color="grey lighten-1" text @click="deleteDialog = false">
            Cancelar
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn color="error" :loading="deleting" @click="confirmDelete">
            Eliminar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Tarjeta Principal con Tabla de Datos -->
    <v-card class="elevation-5 rounded-lg">
      <v-toolbar dark color="primary">
        <v-toolbar-title>
          <v-icon left dark>mdi-pool</v-icon>
          {{ $t('common_areas') }}
        </v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn color="white" outlined class="mr-2" @click="openCreateDialog">
          <v-icon left>mdi-plus</v-icon>
          {{ $t('add_common_area') }}
        </v-btn>
        <v-btn icon @click="fetchCommonAreas" :loading="loading">
          <v-icon>mdi-refresh</v-icon>
        </v-btn>
      </v-toolbar>

      <v-card-text>
        <v-text-field
          v-model="search"
          append-icon="mdi-magnify"
          label="Buscar área común..."
          single-line
          hide-details
          class="mb-4"
          outlined
          dense
          clearable
        ></v-text-field>

        <v-data-table
          :headers="headers"
          :items="commonAreas"
          :loading="loading"
          :search="search"
          class="elevation-1"
          no-data-text="No se encontraron áreas comunes"
          no-results-text="No coincide ninguna área común"
          :footer-props="{
            itemsPerPageOptions: [10, 25, 50, -1],
            itemsPerPageText: 'Filas por página'
          }"
        >
          <!-- Vista miniatura de Imagen -->
          <template v-slot:item.image="{ item }">
            <v-avatar size="44" tile class="my-1 grey lighten-2 rounded">
              <v-img v-if="item.image" :src="item.image" contain></v-img>
              <v-icon v-else color="grey darken-1">mdi-pool</v-icon>
            </v-avatar>
          </template>

          <!-- Color de Calendario -->
          <template v-slot:item.colorCalendar="{ item }">
            <v-chip
              small
              dark
              :style="{ backgroundColor: item.colorCalendar || '#3498DB' }"
            >
              {{ item.colorCalendar || '#3498DB' }}
            </v-chip>
          </template>

          <!-- Requiere Autorización -->
          <template v-slot:item.requiresAuthorization="{ item }">
            <v-chip
              small
              :color="item.requiresAuthorization ? 'warning' : 'grey lighten-1'"
              :dark="item.requiresAuthorization"
            >
              {{ item.requiresAuthorization ? 'Sí' : 'No' }}
            </v-chip>
          </template>

          <!-- Precio -->
          <template v-slot:item.price="{ item }">
            <span v-if="item.hasPrice" class="font-weight-medium success--text">
              ${{ parseFloat(item.price || 0).toFixed(2) }}
            </span>
            <v-chip v-else small color="teal lighten-4" class="teal--text text--darken-3">
              Gratis
            </v-chip>
          </template>

          <!-- Política de Invitados -->
          <template v-slot:item.guestPolicy="{ item }">
            <v-chip
              small
              :color="guestPolicyColor(item.guestPolicy)"
              dark
            >
              {{ guestPolicyLabel(item.guestPolicy) }}
            </v-chip>
          </template>

          <!-- Estado (Activo) -->
          <template v-slot:item.active="{ item }">
            <v-chip
              small
              :color="item.active ? 'success' : 'error'"
              dark
            >
              {{ item.active ? 'Activo' : 'Inactivo' }}
            </v-chip>
          </template>

          <!-- Columna de Acciones: Modificar y Eliminar -->
          <template v-slot:item.actions="{ item }">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  color="blue darken-1"
                  class="mr-1"
                  v-bind="attrs"
                  v-on="on"
                  @click="openEditDialog(item)"
                >
                  <v-icon small>mdi-pencil</v-icon>
                </v-btn>
              </template>
              <span>Modificar</span>
            </v-tooltip>

            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  color="red darken-1"
                  v-bind="attrs"
                  v-on="on"
                  @click="openDeleteDialog(item)"
                >
                  <v-icon small>mdi-delete</v-icon>
                </v-btn>
              </template>
              <span>Eliminar</span>
            </v-tooltip>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
import axios from 'axios'
import store from '@/store'

export default {
  name: 'CommonAreas',
  data () {
    return {
      search: '',
      loading: false,
      submitting: false,
      deleting: false,
      dialog: false,
      deleteDialog: false,
      colorMenu: false,
      valid: true,
      isEditing: false,
      editingId: null,
      itemToDelete: null,
      imageFile: null,
      imagePreview: null,
      // Google Maps
      mapLoading: false,
      geoLocating: false,
      googleMap: null,
      googleMarker: null,
      commonAreas: [],
      headers: [
        { text: 'Imagen', value: 'image', sortable: false, align: 'center' },
        { text: 'Nombre', value: 'name', sortable: true, align: 'start' },
        { text: 'Capacidad', value: 'capacity', sortable: true, align: 'center' },
        { text: 'Máx. Horas', value: 'maxHoursPerReservation', sortable: true, align: 'center' },
        { text: 'Intervalo (Min)', value: 'reservationIntervalMinutes', sortable: true, align: 'center' },
        { text: 'Apertura', value: 'openingTime', sortable: true, align: 'center' },
        { text: 'Cierre', value: 'closingTime', sortable: true, align: 'center' },
        { text: 'Política Invitados', value: 'guestPolicy', sortable: true, align: 'center' },
        { text: 'Req. Autorización', value: 'requiresAuthorization', sortable: true, align: 'center' },
        { text: 'Precio', value: 'price', sortable: true, align: 'center' },
        { text: 'Color', value: 'colorCalendar', sortable: false, align: 'center' },
        { text: 'Acciones', value: 'actions', sortable: false, align: 'center' }
      ],
      guestPolicyOptions: [
        { text: 'Solo Residentes', value: 'RESIDENTS_ONLY' },
        { text: 'Residentes + Invitados', value: 'RESIDENTS_PLUS_GUESTS' },
        { text: 'Abierto al Público', value: 'OPEN' }
      ],
      form: {
        name: '',
        description: '',
        capacity: 30,
        location: '',
        active: true,
        colorCalendar: '#3498DB',
        requiresAuthorization: false,
        maxHoursPerReservation: 3,
        reservationIntervalMinutes: 60,
        hasPrice: false,
        price: 0,
        openingTime: '',
        closingTime: '',
        guestPolicy: 'RESIDENTS_ONLY',
        latitude: null,
        longitude: null
      }
    }
  },
  watch: {
    // Inicializa el mapa cuando el diálogo se abre
    dialog (val) {
      if (val) {
        this.$nextTick(() => {
          this.loadGoogleMaps()
        })
      } else {
        // Destruye la instancia del mapa al cerrar para evitar memory leaks
        this.googleMap = null
        this.googleMarker = null
      }
    }
  },
  mounted () {
    this.fetchCommonAreas()
  },
  methods: {
    getApiHeaders (isMultipart = false) {
      const headers = {
        authorization: 'Token ' + store.getters.getToken
      }
      if (isMultipart) {
        headers['Content-Type'] = 'multipart/form-data'
      } else {
        headers['Content-Type'] = 'application/json'
      }
      if (this.$store && this.$store.state && this.$store.state.language) {
        headers['Accept-Language'] = this.$store.state.language
      }
      return headers
    },

    async fetchCommonAreas () {
      this.loading = true
      try {
        const response = await axios.get('https://residencial-services.intrare.app/reservation/common-areas/', {
          headers: this.getApiHeaders()
        })
        this.commonAreas = Array.isArray(response.data) ? response.data : []
      } catch (error) {
        console.error('Error al obtener áreas comunes:', error)
        this.showSnackbar('Error al obtener las áreas comunes', 'error')
        setTimeout(this.clearSnackBar, 3000)
      } finally {
        this.loading = false
      }
    },

    openCreateDialog () {
      this.isEditing = false
      this.editingId = null
      this.resetForm()
      this.dialog = true
    },

    openEditDialog (item) {
      this.isEditing = true
      this.editingId = item.id
      this.form = {
        name: item.name || '',
        description: item.description || '',
        capacity: item.capacity !== undefined ? item.capacity : 30,
        location: item.location || '',
        active: item.active !== undefined ? item.active : true,
        colorCalendar: item.colorCalendar || '#3498DB',
        requiresAuthorization: item.requiresAuthorization !== undefined ? item.requiresAuthorization : false,
        maxHoursPerReservation: item.maxHoursPerReservation || 3,
        reservationIntervalMinutes: item.reservationIntervalMinutes || 60,
        hasPrice: item.hasPrice !== undefined ? item.hasPrice : false,
        price: item.price ? parseFloat(item.price) : 0,
        openingTime: item.openingTime || '',
        closingTime: item.closingTime || '',
        guestPolicy: item.guestPolicy || 'RESIDENTS_ONLY',
        latitude: item.latitude !== undefined && item.latitude !== null ? parseFloat(item.latitude) : null,
        longitude: item.longitude !== undefined && item.longitude !== null ? parseFloat(item.longitude) : null
      }
      this.imageFile = null
      this.imagePreview = item.image || null
      this.dialog = true
    },

    closeDialog () {
      this.dialog = false
      this.resetForm()
    },

    resetForm () {
      this.form = {
        name: '',
        description: '',
        capacity: 30,
        location: '',
        active: true,
        colorCalendar: '#3498DB',
        requiresAuthorization: false,
        maxHoursPerReservation: 3,
        reservationIntervalMinutes: 60,
        hasPrice: false,
        price: 0,
        openingTime: '',
        closingTime: '',
        guestPolicy: 'RESIDENTS_ONLY',
        latitude: null,
        longitude: null
      }
      this.imageFile = null
      this.imagePreview = null
      if (this.$refs.formRef) {
        this.$refs.formRef.resetValidation()
      }
    },

    onFileChange (file) {
      if (file) {
        this.imagePreview = URL.createObjectURL(file)
      } else {
        this.imagePreview = null
      }
    },

    clearSnackBar: function () {
      this.$store.commit('snackbar_change_color', 'success')
      this.$store.commit('snackbar_activate_false')
    },

    async saveArea () {
      if (this.$refs.formRef && !this.$refs.formRef.validate()) {
        return
      }

      this.submitting = true
      try {
        const formData = new FormData()
        formData.append('name', this.form.name)
        formData.append('description', this.form.description || '')
        formData.append('capacity', this.form.capacity !== undefined ? this.form.capacity : 0)
        formData.append('location', this.form.location || '')
        formData.append('active', this.form.active ? 'true' : 'false')
        formData.append('colorCalendar', this.form.colorCalendar || '#3498DB')
        formData.append('requiresAuthorization', this.form.requiresAuthorization ? 'true' : 'false')
        formData.append('maxHoursPerReservation', this.form.maxHoursPerReservation || 1)
        formData.append('reservationIntervalMinutes', this.form.reservationIntervalMinutes || 60)
        formData.append('hasPrice', this.form.hasPrice ? 'true' : 'false')
        formData.append('price', this.form.hasPrice ? (this.form.price || 0) : 0)
        formData.append('openingTime', this.form.openingTime || '')
        formData.append('closingTime', this.form.closingTime || '')
        formData.append('guestPolicy', this.form.guestPolicy || 'RESIDENTS_ONLY')
        if (this.form.latitude !== null && this.form.latitude !== '') {
          formData.append('latitude', this.form.latitude)
        }
        if (this.form.longitude !== null && this.form.longitude !== '') {
          formData.append('longitude', this.form.longitude)
        }
        // location guarda el link de Google Maps generado automáticamente
        formData.append('location', this.form.location || '')

        if (this.imageFile) {
          formData.append('image', this.imageFile)
        }

        if (this.isEditing) {
          await axios.patch(`https://residencial-services.intrare.app/reservation/common-areas/${this.editingId}/`, formData, {
            headers: this.getApiHeaders(true)
          })
          this.showSnackbar('Área común actualizada correctamente', 'success')
          setTimeout(this.clearSnackBar, 3000)
        } else {
          await axios.post('https://residencial-services.intrare.app/reservation/common-areas/', formData, {
            headers: this.getApiHeaders(true)
          })
          this.showSnackbar('Área común creada correctamente', 'success')
          setTimeout(this.clearSnackBar, 3000)
        }

        this.closeDialog()
        this.fetchCommonAreas()
      } catch (error) {
        console.error('Error al guardar área común:', error)
        this.showSnackbar('Error al guardar el área común', 'error')
        setTimeout(this.clearSnackBar, 3000)
      } finally {
        this.submitting = false
      }
    },

    openDeleteDialog (item) {
      this.itemToDelete = item
      this.deleteDialog = true
    },

    async confirmDelete () {
      if (!this.itemToDelete) return
      this.deleting = true
      try {
        await axios.delete(`https://residencial-services.intrare.app/reservation/common-areas/${this.itemToDelete.id}/`, {
          headers: this.getApiHeaders()
        })
        this.showSnackbar('Área común eliminada correctamente', 'warning')
        setTimeout(this.clearSnackBar, 3000)
        this.deleteDialog = false
        this.itemToDelete = null
        this.fetchCommonAreas()
      } catch (error) {
        console.error('Error al eliminar área común:', error)
        this.showSnackbar('Error al eliminar el área común', 'error')
        setTimeout(this.clearSnackBar, 3000)
      } finally {
        this.deleting = false
      }
    },

    showSnackbar (message, color = 'success') {
      if (this.$store && this.$store.commit) {
        this.$store.commit('snackbar_activate_true')
        this.$store.commit('snackbar_change_color', color)
        this.$store.commit('snackbar_change_message', message)
      }
    },

    guestPolicyLabel (policy) {
      const map = {
        RESIDENTS_ONLY: 'Solo Residentes',
        RESIDENTS_PLUS_GUESTS: 'Residentes + Invitados',
        OPEN: 'Abierto al Público'
      }
      return map[policy] || policy || '—'
    },

    guestPolicyColor (policy) {
      const map = {
        RESIDENTS_ONLY: 'indigo',
        RESIDENTS_PLUS_GUESTS: 'teal',
        OPEN: 'deep-orange'
      }
      return map[policy] || 'grey'
    },

    // =============================================
    // MÉTODOS DE GOOGLE MAPS
    // =============================================

    /**
     * Carga dinámicamente el script de Google Maps si no está ya disponible.
     */
    loadGoogleMaps () {
      if (window.google && window.google.maps) {
        this.initMap()
        return
      }
      this.mapLoading = true
      const apiKey = process.env.VUE_APP_GOOGLE_MAPS_KEY || ''
      if (!apiKey || apiKey === 'TU_API_KEY_AQUI') {
        console.warn('Google Maps API Key no configurada. Agrega VUE_APP_GOOGLE_MAPS_KEY en .env.local')
        this.mapLoading = false
        return
      }
      const callbackName = '__gmapsInit'
      window[callbackName] = () => {
        this.mapLoading = false
        this.initMap()
      }
      const script = document.createElement('script')
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&callback=${callbackName}&loading=async`
      script.async = true
      script.defer = true
      script.onerror = () => {
        this.mapLoading = false
        this.showSnackbar('Error al cargar Google Maps. Verifica tu API Key.', 'error')
        setTimeout(this.clearSnackBar, 4000)
      }
      document.head.appendChild(script)
    },

    /**
     * Inicializa el mapa de Google Maps en el contenedor ref="mapCanvas".
     * Centra en las coordenadas del form si existen, de lo contrario usa CDMX.
     */
    initMap () {
      if (!this.$refs.mapCanvas || !window.google) return
      const defaultCenter = {
        lat: this.form.latitude ? parseFloat(this.form.latitude) : 19.4326,
        lng: this.form.longitude ? parseFloat(this.form.longitude) : -99.1332
      }
      const zoom = (this.form.latitude && this.form.longitude) ? 16 : 13
      this.googleMap = new window.google.maps.Map(this.$refs.mapCanvas, {
        center: defaultCenter,
        zoom,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: true,
        zoomControlOptions: {
          position: window.google.maps.ControlPosition.RIGHT_CENTER
        }
      })
      // Si ya hay coordenadas guardadas, coloca el marker
      if (this.form.latitude && this.form.longitude) {
        this.placeMarker(defaultCenter.lat, defaultCenter.lng, false)
      }
      // Listener: click en el mapa para colocar el pin
      this.googleMap.addListener('click', (event) => {
        const lat = event.latLng.lat()
        const lng = event.latLng.lng()
        this.placeMarker(lat, lng)
      })
    },

    /**
     * Coloca o mueve el marcador en las coordenadas dadas.
     * Actualiza form.latitude, form.longitude y form.location.
     * @param {number} lat - Latitud
     * @param {number} lng - Longitud
     * @param {boolean} updateForm - Si true, actualiza el formulario (default: true)
     */
    placeMarker (lat, lng, updateForm = true) {
      if (!this.googleMap) return
      const position = { lat, lng }
      if (this.googleMarker) {
        this.googleMarker.setPosition(position)
      } else {
        this.googleMarker = new window.google.maps.Marker({
          position,
          map: this.googleMap,
          draggable: true,
          animation: window.google.maps.Animation.DROP,
          title: this.form.name || 'Área Común'
        })
        // Permitir mover el marcador arrastrándolo
        this.googleMarker.addListener('dragend', (event) => {
          const newLat = event.latLng.lat()
          const newLng = event.latLng.lng()
          this.form.latitude = parseFloat(newLat.toFixed(7))
          this.form.longitude = parseFloat(newLng.toFixed(7))
          this.form.location = this.buildGoogleMapsLink(newLat, newLng)
        })
      }
      this.googleMap.panTo(position)
      if (updateForm) {
        this.form.latitude = parseFloat(lat.toFixed(7))
        this.form.longitude = parseFloat(lng.toFixed(7))
        this.form.location = this.buildGoogleMapsLink(lat, lng)
      }
    },

    /**
     * Genera el link de Google Maps a partir de coordenadas.
     */
    buildGoogleMapsLink (lat, lng) {
      return `https://www.google.com/maps?q=${lat},${lng}`
    },

    /**
     * Usa la geolocalización del navegador para centrar el mapa y colocar el pin.
     */
    useMyLocation () {
      if (!navigator.geolocation) {
        this.showSnackbar('Tu navegador no soporta geolocalización', 'warning')
        setTimeout(this.clearSnackBar, 3000)
        return
      }
      this.geoLocating = true
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const lat = position.coords.latitude
          const lng = position.coords.longitude
          this.placeMarker(lat, lng)
          if (this.googleMap) {
            this.googleMap.setZoom(17)
          }
          this.geoLocating = false
        },
        (error) => {
          console.error('Geolocalización error:', error)
          this.showSnackbar('No se pudo obtener tu ubicación', 'error')
          setTimeout(this.clearSnackBar, 3000)
          this.geoLocating = false
        },
        { enableHighAccuracy: true, timeout: 10000 }
      )
    },

    /**
     * Reacciona a la edición manual de los campos de latitud/longitud.
     * Mueve el pin si ambos valores son válidos.
     */
    onLatLngInput () {
      const lat = parseFloat(this.form.latitude)
      const lng = parseFloat(this.form.longitude)
      if (!isNaN(lat) && !isNaN(lng) &&
          lat >= -90 && lat <= 90 &&
          lng >= -180 && lng <= 180) {
        this.placeMarker(lat, lng, false)
        this.form.location = this.buildGoogleMapsLink(lat, lng)
        if (this.googleMap) {
          this.googleMap.panTo({ lat, lng })
        }
      }
    },

    /**
     * Limpia las coordenadas y el marcador del mapa.
     */
    clearMapLocation () {
      this.form.latitude = null
      this.form.longitude = null
      this.form.location = ''
      if (this.googleMarker) {
        this.googleMarker.setMap(null)
        this.googleMarker = null
      }
    },

    /**
     * Abre el link de Google Maps en una nueva pestaña.
     */
    openMapLink () {
      if (this.form.location) {
        window.open(this.form.location, '_blank')
      }
    }
  }
}
</script>

<style scoped>
.v-card {
  border-radius: 12px;
}

/* Contenedor del mapa Google Maps */
.map-canvas {
  width: 100%;
  height: 280px;
  border-radius: 8px;
  overflow: hidden;
  background-color: #e8ecf0;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
}

.map-canvas--loading {
  background-color: #f5f5f5;
}

.map-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: row;
}

/* Tarjeta que contiene la sección del mapa */
.map-section-card {
  border: 1px solid rgba(0, 0, 0, 0.15) !important;
}
</style>
